import { Component, inject, OnInit } from '@angular/core';
import { TranslocoRootModule } from '../../transloco-root.module';
import { watchIsLoggedIn, login, logout } from 'shell-browser/auth.js';
import { NgShellRxjsWrapperService } from '../../shell/ng-shell-wrapper.service';
import { Observable, distinctUntilChanged, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { NotificationSearchComponent } from '../notification-search/notification-search.component';
import { OneWebComponentsAngularModule } from '@one/angular';
import { NotificationFilterComponent } from '../notification-filter/notification-filter.component';
import { NotificationTableComponent } from '../notification-table/notification-table.component';
import { CustomerNotificationService } from './customer-notification.service';
import { Router } from '@angular/router';
import { LinkService } from '../../core/services/link.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-main-page',
  templateUrl: './main-page.component.html',
  styleUrls: ['./main-page.component.scss'],
  standalone: true,
  imports: [TranslocoRootModule, NotificationSearchComponent, OneWebComponentsAngularModule, NotificationFilterComponent, NotificationTableComponent],
})

export class MainPageComponent implements OnInit {
  readonly ngShellService = inject(NgShellRxjsWrapperService);
  customerservice = inject(CustomerNotificationService);
  http = inject(HttpClient);
  router = inject(Router);
  linkService: LinkService = inject(LinkService);
  isLoggedIn$: Observable<boolean>;
  isLoggedIn: boolean = false;
  loading: boolean = false;
  notificationsList: any;
  accSelected: string;
  filterOptions: { types: string[]; statuses: string[] } 
  filterOptSel: { types: string[]; statuses: string[] } 
  searchValueSel: any;
  spinBool:boolean = false;
  
  ngOnInit(): void {

    const requestedNotification = this.getRequestedNotificationFromUrl();
    if (requestedNotification) {
      this.redirectToRequestedNotification(requestedNotification);
      return;
    }

    this.getIsLoggedIn();

    this.isLoggedIn$
    .pipe(
        distinctUntilChanged(), // Only emit when value changes
        untilDestroyed(this)
      )
    .subscribe(async isLoggedIn => {
      this.isLoggedIn = isLoggedIn;
      if (isLoggedIn) {
        this.performLoggedInActions();
      }else {
        this.performLoggedOutActions();
      }
    });
  }

  private getRequestedNotificationFromUrl(): any {
    const urlQueryString = window.location.search;
    const parameters = new URLSearchParams(urlQueryString);

    // Set parameters to lower case to avoid issues
    const lowerCaseParameters = new Map<string, string>();
    if (parameters.size) {
      parameters.forEach((value, key) => {
        lowerCaseParameters.set(key.toLowerCase(), value);
      });
    }

    if (lowerCaseParameters.get('notificationid')) {
      const requestedNotification = {
        notificationId: lowerCaseParameters.get('notificationid'),
        contactNotificationId: lowerCaseParameters.get('contactnotificationid'),
        publicKeyToken: lowerCaseParameters.get('publickeytoken'),
        country: lowerCaseParameters.get('country'),
        lang: lowerCaseParameters.get('lang')
      }
      return requestedNotification;
    }
  }

  private redirectToRequestedNotification(requestedNotification: any): void {
    var viewUrl = 'customer-notification-details';
    if (requestedNotification.country && requestedNotification.lang) {
      viewUrl = `${requestedNotification.country}/${requestedNotification.lang.replace('_', '-')}/${viewUrl}`;
    }
    this.router.navigate([viewUrl], { 
      queryParams: { 
        publicKeyToken: requestedNotification.publicKeyToken,
        notificationId: requestedNotification.notificationId,
        contactNotificationId: requestedNotification.contactNotificationId,
      } });
  }
  
  private getIsLoggedIn(): void {
    this.isLoggedIn$ = this.ngShellService.toObservable(watchIsLoggedIn);
  }

  private async performLoggedInActions(): Promise<void> {
    this.spinBool = true;
    await this.getNotificationDetails();
  }

  private performLoggedOutActions(): void {
  }

  private getNotificationDetails(): void {
    // One subscription to the account selector, cleaned up with the component.
    // - distinctUntilChanged: ignore identical consecutive selections (e.g. clicking the
    //   already-selected account) so a single load triggers a single API call.
    // - switchMap: cancel any in-flight request when a new account is picked,
    //   guaranteeing only one HTTP call is active per Account View load.
    // - untilDestroyed: prevent the leak that previously stacked subscribers across
    //   navigations (main -> details -> main) and produced N duplicate calls per click.
    this.customerservice.accountSelector$
      .pipe(
        distinctUntilChanged(),
        tap(res => { this.accSelected = res; }),
        switchMap(res => {
          const request$ = (res === 'My notifications')
            ? this.customerservice.getNotificationTableData(['all'], ['all'])
            : this.customerservice.getAccountDetails(res, ['all'], ['all']);
          return request$.pipe(
            map(data => ({ data, isError: false })),
            catchError(err => {
              const label = (res === 'My notifications') ? 'notifications' : 'account details';
              console.log(err, `error fetching ${label}`);
              return of({ data: [] as any[], isError: true });
            })
          );
        }),
        untilDestroyed(this)
      )
      .subscribe(({ data, isError }) => {
        this.notificationsList = data;
        this.filterOptions = isError
          ? { types: [], statuses: [] }
          : this.extractFilters(data);
        this.loading = true;
        this.spinBool = false;
      });
  }

  extractFilters(data: any[]): { types: string[], statuses: string[] } {
    return  {
      types: this.extractTypes(data),
      statuses: this.extractStatuses(data)
    }
  }

  private  extractTypes(data: any[]): string[] {
    const types = data.map(item => item.Type);
    return Array.from(new Set(types));
  }

  private extractStatuses(data: any[]): string[] {
    const statuses = data.map(item => (item.CustomerStatus || item.Status));
    // 'Not Read' is a frontend-only status driven by the per-row toggle.
    // Always offer it so users can filter to the items they have manually marked.
    return [...Array.from(new Set(statuses)), 'Not Read', 'Completed'];
  }

  filterOptInp(data){
    this.filterOptSel = data;
  }

  searchValueInp(data){
    this.searchValueSel = data;
  }
}
